/**
 * Invite a friend (plan.md step 89, the user, 2026-10-29: "a very quick way to share the app …
 * a barcode with our barcode generator, same as the tickets but only the MyRave logo on it, and
 * also share via … maybe AirDrop").
 *
 * One screen, four ways: the **QR code** (MyRave's design, `ticketCodes.drawCode` with no rave
 * logo, read back before it is shown), **Share…** (the phone's own sheet — AirDrop, Quick Share,
 * every app; in the phone app the shell's, `mobile/src/shareShim.ts`), **WhatsApp**, and **Copy
 * link**. Each way's link says how it was shared (`?via=`), and the boards count it.
 *
 * Inside a rave it is that rave's invite; anywhere else MyRave's; signed out, this device's guest
 * invite (`lib/invites.ts`). The code and its drawing are kept, so the screen opens offline.
 *
 * Opened from the "Invite a friend" row of every menu, or by a swipe up with four fingers or more
 * — a whole hand — anywhere in the app (`lib/fullHandSwipe.ts`, the user, 2026-10-29) — not on the
 * sign-in, admin, console or support
 * hosts, and never in a framed preview. In the phone app the screen goes to full brightness
 * while it is open.
 *
 * `InviteFriendProvider` also carries the invite TRACKING that is not a rave's own: noting a
 * MyRave invite this device arrived with, its open, its claim once signed in, and handing a
 * guest's invites over to the account (`adoptGuestInvites`).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { InviteVia } from '@myrave/shared';
import { useT } from '@/i18n';
import { RaveMenuName } from './RaveMenuName';
import { environmentMark, sharedFromHere } from '@/lib/environment';
import { LogoMark, Sheet, useToast } from '@/ui';
import { useAuth } from './AuthProvider';
import { useTenant } from './TenantProvider';
import { isAccountHost, isAppHost, platformUrl } from '@/lib/tenant';
import { isFramed } from '@/lib/framed';
import { inPreview } from '@/lib/preview';
import { raiseBrightness } from '@/lib/nativeBridge';
import { useFullHandSwipe } from '@/lib/fullHandSwipe';
import { adoptGuestInvites, drawInviteQr as drawQr, fetchInvite, inviteLinkVia, inviteOwner, keepInviteQr, keptInvite, type KeptInvite } from '@/lib/invites';
import { claimPendingMyRaveInvite, noteInviteArrival, reportInviteOpen } from '@/lib/inviteTracking';
import './InviteFriend.css';

interface InviteFriendApi { open: () => void }

const Ctx = createContext<InviteFriendApi | null>(null);

/** Opens "Invite a friend" — null outside the provider (a test's own tree). */
export function useInviteFriend(): InviteFriendApi | null {
  return useContext(Ctx);
}

export function InviteFriendProvider({ children }: { children: ReactNode }) {
  const { tenant, scope } = useTenant();
  const { user, loading, offline } = useAuth();
  const [shown, setShown] = useState(false);
  const inRave = scope === 'festival';
  const editionId = inRave ? tenant?.editionId ?? null : null;
  const framed = isFramed() || inPreview();
  // The app's own hosts: the app, a rave, the account — not sign-in, admin, console or support.
  const here = !framed && (inRave || isAppHost() || isAccountHost());

  const open = useCallback(() => setShown(true), []);
  useFullHandSwipe(here && !shown, open);

  // A MyRave invite this device arrived with (a rave's own host notes its own: `InviteTracking`).
  const [arrived] = useState(() => (inRave ? null : noteInviteArrival()));
  useEffect(() => { if (!loading && arrived) reportInviteOpen(arrived); }, [loading, arrived]);
  const letIn = Boolean(user && (user.approved || user.role === 'SUPER_ADMIN') && !offline);
  useEffect(() => {
    if (!letIn) return;
    void adoptGuestInvites();
    if (!inRave) void claimPendingMyRaveInvite();
  }, [letIn, inRave]);

  const api = useMemo(() => ({ open }), [open]);
  return (
    <Ctx.Provider value={api}>
      {children}
      {shown && (
        <InviteFriendSheet
          editionId={editionId}
          rave={inRave && tenant ? { festivalName: tenant.festivalName, logoUrl: tenant.logoUrl ?? null } : null}
          onClose={() => setShown(false)}
        />
      )}
    </Ctx.Provider>
  );
}

const touchScreen = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

/** How long a kept invite waits for the server's answer before it is shown anyway (no signal, a slow one). */
const KEPT_WAIT_MS = 1500;

export function InviteFriendSheet({ editionId, rave: here = null, onClose }: {
  editionId: string | null;
  /** Inside a rave: its name and logo — the logo at the top (else the name) and inside the QR code. */
  rave?: { festivalName: string; logoUrl: string | null } | null;
  onClose: () => void;
}) {
  const tr = useT();
  const toast = useToast();
  const { user, loading: authLoading } = useAuth();
  const owner = inviteOwner(user?.id);
  // A framed preview (the setup wizard's phone) shows the plain address and asks for nobody's code.
  const framed = isFramed() || inPreview();
  /*
   * ONE code on screen: the kept one waits for who is asking and for the server's answer (at most `KEPT_WAIT_MS`) before
   * it is shown, so a stale kept code is never drawn and then replaced; with no signal, or no answer in time, it shows.
   */
  const [waited, setWaited] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setWaited(true), KEPT_WAIT_MS);
    return () => clearTimeout(t);
  }, []);
  const known = !authLoading || waited;
  const [kept] = useState(() => (framed ? null : keptInvite(editionId, owner)));
  const q = useQuery({
    queryKey: ['invite', editionId, owner],
    queryFn: () => fetchInvite(editionId, owner),
    initialData: kept ?? undefined,
    initialDataUpdatedAt: 0,
    // Only once it is known who is asking — a guest's code first, then the account's, swapped the picture (the user,
    // 2026-11-02: "invite a friend showed the old barcode, then replaced it with the new").
    enabled: !framed && known,
    retry: false,
  });
  const offlineNow = typeof navigator !== 'undefined' && navigator.onLine === false;
  const settled = framed || (known && (q.isFetched || q.isError || waited || offlineNow));
  const invite: KeptInvite | null = q.data ?? null;
  // Never had a code (offline the first time): the plain address, untracked — still a way in.
  const plain = editionId ? `${window.location.origin}/` : `${platformUrl()}/`;
  const qrUrl = invite ? inviteLinkVia(invite, 'qr') : plain;

  const logoUrl = here?.logoUrl ?? null;
  // A kept drawing serves only when it was drawn with this logo (or none, outside a rave).
  const keptQr = invite?.qr && (invite.qrLogo ?? null) === logoUrl ? invite.qr : null;
  const [svg, setSvg] = useState<string | null>(() => keptQr);
  const [drawFailed, setDrawFailed] = useState(false);
  const shownSvg = settled ? svg : null;
  useEffect(() => {
    if (keptQr) { setSvg(keptQr); return undefined; }
    let live = true;
    setDrawFailed(false);
    drawQr(qrUrl, logoUrl).then((drawn) => {
      if (!live) return;
      if (!drawn) { setDrawFailed(true); return; }
      setSvg(drawn);
      if (invite) keepInviteQr(editionId, invite, drawn, logoUrl);
    }, () => { if (live) setDrawFailed(true); });
    return () => { live = false; };
  }, [qrUrl, invite, editionId, logoUrl, keptQr]);

  // Full brightness while the code is on screen (the phone app), the person's own after.
  useEffect(() => {
    raiseBrightness(true);
    return () => raiseBrightness(false);
  }, []);

  const rave = invite?.raveName ?? null;
  // From dev or staging the invitation says it is a test first (step 96) — a staging link is not sent as the real one.
  const words = sharedFromHere(rave
    ? tr('Come to {rave} with me — the lineup, the map and who is playing, on MyRave.', { rave })
    : tr('Join me on MyRave — every rave’s lineup, map and who is playing, even with no signal.'), tr('Test version of MyRave (opening soon):'));
  const mark = environmentMark();
  const link = (via: InviteVia) => (invite ? inviteLinkVia(invite, via) : plain);

  const copy = async () => {
    const url = link('copy');
    try {
      // The link alone (the user, 0.14.76 walk: "it's copying the entire share message and not just the link") —
      // the words go with Share… and WhatsApp, in the person's language.
      await navigator.clipboard.writeText(url);
      toast.show(tr('Link copied'));
    } catch {
      toast.show(tr('Send a friend this link: {url}', { url }), { duration: 8000 });
    }
  };
  const share = async () => {
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: rave ?? 'MyRave', text: words, url: link('share') });
        return;
      } catch (err) {
        // A dismissed sheet is just dismissed — nothing is copied behind somebody's back.
        if ((err as { name?: string } | null)?.name === 'AbortError') return;
      }
    }
    await copy();
  };
  const whatsApp = `https://wa.me/?text=${encodeURIComponent(`${words} ${link('wa')}`)}`;

  return (
    <Sheet label={tr('Invite a friend')} onClose={onClose} closeLabel={tr('Close')} className="invite-sheet">
      {/* A close button you can see (the user, 2026-10-29: "missing X closing window") — the scrim
          and Escape close it too, but neither is something a thumb or a screen reader finds. */}
      <button type="button" className="invite-close" onClick={onClose} aria-label={tr('Close')}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
      </button>
      <div className="invite-body">
        <h2 className="invite-title">{tr('Invite a friend')}</h2>
        {/* The rave: its logo, else its name — then the edition, as everywhere (the user, 2026-10-31). */}
        {here && rave && (
          <p className="invite-rave"><RaveMenuName festivalName={here.festivalName} editionName={rave} logoUrl={here.logoUrl} /></p>
        )}
        {invite?.inviterName && <p className="invite-from" dir="auto">{tr('{name} invites you', { name: invite.inviterName })}</p>}
        {mark && <p className="invite-env" data-env={mark}>{mark} · {tr('Test version')}</p>}
        <div className="invite-qr" data-state={shownSvg ? 'drawn' : drawFailed && settled ? 'failed' : 'drawing'}>
          {shownSvg ? (
            <img
              src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(shownSvg)}`}
              alt={rave ? tr('Scan to join {rave} on MyRave', { rave }) : tr('Scan to get MyRave')}
            />
          ) : drawFailed && settled ? (
            <span className="invite-qr-link" dir="ltr">{qrUrl}</span>
          ) : (
            // MyRave's mark, its bars moving, while the code is on its way (the user, 2026-11-02).
            <span className="invite-qr-wait" role="img" aria-label={tr('One moment…')}><LogoMark size={56} spin /></span>
          )}
        </div>
        <p className="invite-scan" dir="auto">{rave ? tr('Scan to join {rave} on MyRave', { rave }) : tr('Scan to get MyRave')}</p>
        <div className="invite-ways">
          <button type="button" className="invite-way is-primary" onClick={() => void share()}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M16 6l-4-4-4 4M12 2v13" /></svg>
            {tr('Share…')}
          </button>
          <a className="invite-way is-wa" href={whatsApp} target="_blank" rel="noopener noreferrer">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.2-.4.7-1.4.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4c1.7.7 2.3.8 3.2.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2l-.5-.3z" /></svg>
            WhatsApp
          </a>
          <button type="button" className="invite-way" onClick={() => void copy()}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" /></svg>
            {tr('Copy link')}
          </button>
        </div>
        {touchScreen() && <p className="invite-tip">{tr('Next time: swipe up the screen with four fingers or more.')}</p>}
      </div>
    </Sheet>
  );
}
